import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { confirmPasswordReset } from 'firebase/auth'
import { auth } from '@/config/firebase'
import { useToast } from '@/context/ToastContext'
import {
  validatePassword,
  PASSWORD_REQUIREMENT_MESSAGE,
} from '@common/utils/password-validator'

function validateNewPassword(
  newPassword: string,
  confirmPassword: string,
): { ok: true; password: string } | { ok: false; newPasswordError?: string; confirmError?: string } {
  const trimmedNew = newPassword.trim()
  const trimmedConfirm = confirmPassword.trim()

  if (!trimmedNew) {
    return { ok: false, newPasswordError: '新しいパスワードは必須です' }
  }
  if (!validatePassword(trimmedNew)) {
    return { ok: false, newPasswordError: PASSWORD_REQUIREMENT_MESSAGE }
  }

  if (!trimmedConfirm) {
    return { ok: false, confirmError: 'パスワードの再入力は必須です' }
  }
  if (trimmedNew !== trimmedConfirm) {
    return { ok: false, confirmError: 'パスワードが一致しません' }
  }

  return { ok: true, password: trimmedNew }
}

export const useResetPassword = () => {
  const [searchParams] = useSearchParams()
  const oobCode = searchParams.get('oobCode') ?? ''
  const navigate = useNavigate()
  const { showSuccessMessage, showErrorMessage } = useToast()

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null)
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setNewPasswordError(null)
    setConfirmPasswordError(null)

    const validation = validateNewPassword(newPassword, confirmPassword)
    if (!validation.ok) {
      if (validation.newPasswordError) setNewPasswordError(validation.newPasswordError)
      if (validation.confirmError) setConfirmPasswordError(validation.confirmError)
      return
    }

    if (!oobCode) {
      showErrorMessage('無効なリンクです。再度パスワード再設定メールを請求してください。')
      return
    }

    setIsLoading(true)
    try {
      await confirmPasswordReset(auth, oobCode, validation.password)
      showSuccessMessage('パスワードを変更しました。新しいパスワードでログインしてください。')
      navigate('/login', { replace: true })
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code
      if (code === 'auth/expired-action-code') {
        showErrorMessage('リンクの有効期限が切れています。再度パスワード再設定メールを送信してください。')
        setNewPasswordError('リンクの有効期限が切れています')
      } else if (code === 'auth/invalid-action-code') {
        showErrorMessage('無効なリンクです。再度パスワード再設定メールを送信してください。')
        setNewPasswordError('無効なリンクです')
      } else if (code === 'auth/weak-password') {
        setNewPasswordError(PASSWORD_REQUIREMENT_MESSAGE)
        showErrorMessage('パスワードが弱すぎます')
      } else {
        console.error('Reset password error:', err)
        showErrorMessage('パスワードの変更に失敗しました。もう一度お試しください。')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return {
    oobCode,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    showNewPassword,
    showConfirmPassword,
    isLoading,
    newPasswordError,
    confirmPasswordError,
    setNewPasswordError,
    setConfirmPasswordError,
    handleSubmit,
    toggleNewPasswordVisibility: () => setShowNewPassword((v) => !v),
    toggleConfirmPasswordVisibility: () => setShowConfirmPassword((v) => !v),
  }
}
